import {
  collection,
  doc,
  addDoc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Call Service - 1:1 Video/Audio Calls
 *
 * Works with iOS and Web clients.
 * Uses Firebase for signaling and call state.
 * Cloud Functions handle push notifications and timeouts.
 */

// ============================================
// CALL OPERATIONS
// ============================================

/**
 * Initiate a call to another user
 * Creates a call document that triggers Cloud Function for push notification
 * @param {string} callerId - Caller's user ID
 * @param {string} calleeId - Callee's user ID
 * @param {string} callerName - Caller's display name
 * @param {string} callerPhotoUrl - Caller's photo URL
 * @param {string} callType - 'video' or 'audio'
 * @param {string} chatId - Optional chat ID for call log messages
 * @param {string} calleeName - Callee's display name (for call log)
 */
export async function initiateCall(callerId, calleeId, callerName, callerPhotoUrl, callType = 'video', chatId = null, calleeName = null) {
  const callData = {
    callerId,
    calleeId,
    callerName: callerName || 'Unknown',
    callerPhotoUrl: callerPhotoUrl || '',
    calleeName: calleeName || null,
    type: callType,
    status: 'ringing',
    chatId: chatId || null,
    createdAt: serverTimestamp(),
    answeredAt: null,
    endedAt: null,
  };

  const callRef = await addDoc(collection(db, 'calls'), callData);
  return { id: callRef.id, ...callData };
}

/**
 * Answer an incoming call
 */
export async function answerCall(callId) {
  const callRef = doc(db, 'calls', callId);
  await updateDoc(callRef, {
    status: 'answered',
    answeredAt: serverTimestamp(),
  });
}

/**
 * Decline an incoming call
 */
export async function declineCall(callId) {
  const callRef = doc(db, 'calls', callId);
  await updateDoc(callRef, {
    status: 'declined',
    endedAt: serverTimestamp(),
  });
}

/**
 * End an active call
 */
export async function endCall(callId) {
  const callRef = doc(db, 'calls', callId);
  await updateDoc(callRef, {
    status: 'ended',
    endedAt: serverTimestamp(),
  });
}

/**
 * Cancel an outgoing call (before answered)
 */
export async function cancelCall(callId) {
  const callRef = doc(db, 'calls', callId);
  await updateDoc(callRef, {
    status: 'ended',
    endedAt: serverTimestamp(),
  });
}

/**
 * Get call document
 */
export async function getCall(callId) {
  const callRef = doc(db, 'calls', callId);
  const callSnap = await getDoc(callRef);
  if (callSnap.exists()) {
    return { id: callSnap.id, ...callSnap.data() };
  }
  return null;
}

/**
 * Listen for incoming calls for a user
 */
export function listenForIncomingCalls(userId, onIncomingCall) {
  const callsRef = collection(db, 'calls');
  const q = query(
    callsRef,
    where('calleeId', '==', userId),
    where('status', '==', 'ringing')
  );

  return onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added') {
        const call = { id: change.doc.id, ...change.doc.data() };
        onIncomingCall(call);
      }
    });
  });
}

/**
 * Listen for call status changes
 */
export function listenForCallChanges(callId, onCallChange) {
  const callRef = doc(db, 'calls', callId);

  return onSnapshot(callRef, (snapshot) => {
    if (snapshot.exists()) {
      onCallChange({ id: snapshot.id, ...snapshot.data() });
    } else {
      onCallChange(null);
    }
  });
}

// ============================================
// WEBRTC SIGNALING FOR 1:1 CALLS
// ============================================

/**
 * Send WebRTC offer
 */
export async function sendOffer(callId, fromUserId, toUserId, offer) {
  const offerRef = doc(db, 'calls', callId, 'signaling', toUserId, 'offers', fromUserId);
  await setDoc(offerRef, {
    sdp: offer.sdp,
    type: offer.type,
    createdAt: serverTimestamp(),
  });
}

/**
 * Send WebRTC answer
 */
export async function sendAnswer(callId, fromUserId, toUserId, answer) {
  const answerRef = doc(db, 'calls', callId, 'signaling', toUserId, 'answers', fromUserId);
  await setDoc(answerRef, {
    sdp: answer.sdp,
    type: answer.type,
    createdAt: serverTimestamp(),
  });
}

/**
 * Send ICE candidate
 */
export async function sendIceCandidate(callId, fromUserId, toUserId, candidate) {
  const candidateRef = doc(
    db,
    'calls',
    callId,
    'signaling',
    toUserId,
    'candidates',
    `${fromUserId}_${Date.now()}`
  );
  await setDoc(candidateRef, {
    candidate: candidate.candidate,
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex,
    createdAt: serverTimestamp(),
  });
}

/**
 * Listen for WebRTC offers
 */
export function listenForOffers(callId, userId, onOffer) {
  const offersRef = collection(db, 'calls', callId, 'signaling', userId, 'offers');

  return onSnapshot(offersRef, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added') {
        const offer = change.doc.data();
        const fromUserId = change.doc.id;
        onOffer(fromUserId, offer);
        // Clean up after processing
        deleteDoc(change.doc.ref);
      }
    });
  });
}

/**
 * Listen for WebRTC answers
 */
export function listenForAnswers(callId, userId, onAnswer) {
  const answersRef = collection(db, 'calls', callId, 'signaling', userId, 'answers');

  return onSnapshot(answersRef, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added') {
        const answer = change.doc.data();
        const fromUserId = change.doc.id;
        onAnswer(fromUserId, answer);
        // Clean up after processing
        deleteDoc(change.doc.ref);
      }
    });
  });
}

/**
 * Listen for ICE candidates
 */
export function listenForIceCandidates(callId, userId, onCandidate) {
  const candidatesRef = collection(db, 'calls', callId, 'signaling', userId, 'candidates');

  return onSnapshot(candidatesRef, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === 'added') {
        const candidate = change.doc.data();
        onCandidate(candidate);
        // Clean up after processing
        deleteDoc(change.doc.ref);
      }
    });
  });
}

